// src/pages/admin/PortfolioManagement.js - User Analysis Permissions with Read-Write/Read-Only + AI Mode
import React, { useState, useEffect, useCallback } from 'react';
import { userService, companyService } from '../../services/apiServices';
import { analysisService } from '../../services/analysisService';
import { analysisSharingService } from '../../services/analysisSharingService';
import { aiSettingsService } from '../../services/aiSettingsService';
import { useAuth } from '../../context/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showSuccessToast, showErrorToast } from '../../utils/toastUtils';
import {
    MagnifyingGlassIcon,
    UserIcon,
    DocumentTextIcon,
    ChevronDownIcon,
    ChevronRightIcon,
    CheckIcon,
    XMarkIcon,
    UserGroupIcon,
    ShieldCheckIcon,
    EyeIcon,
    EyeSlashIcon,
    PencilSquareIcon,
    LockClosedIcon,
    CloudIcon,
    ServerIcon,
    CpuChipIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';

const PortfolioManagement = () => {
    const { user: currentUser } = useAuth();

    // State for users and analyses
    const [users, setUsers] = useState([]);
    const [analyses, setAnalyses] = useState([]);
    // userPermissions: { [userId]: { [analysisId]: 'read-write' | 'read-only' | null } }
    const [userPermissions, setUserPermissions] = useState({});

    // Loading and error states
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState(null);

    // Search state
    const [searchTerm, setSearchTerm] = useState('');

    // UI state for enhanced interactions
    const [expandedUsers, setExpandedUsers] = useState({});
    const [selectedUsers, setSelectedUsers] = useState(new Set());
    const [bulkMode, setBulkMode] = useState(false);
    const [animatingCards, setAnimatingCards] = useState(new Set());
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [filterMode, setFilterMode] = useState('all'); // 'all', 'granted', 'owned'
    const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

    // AI Mode state - per analysis
    // Format: { [analysisId]: 'external' | 'local' }
    const [analysisAISettings, setAnalysisAISettings] = useState({});
    // Per-analysis local model selection: { [analysisId]: 'gemma3:12b' | 'llama3.2' | ... }
    const [analysisLocalModels, setAnalysisLocalModels] = useState({});
    const [ollamaStatus, setOllamaStatus] = useState({ available: false, models: [] });
    const [isLoadingOllama, setIsLoadingOllama] = useState(true);

    // Superadmin-only: cross-company scope (admin path untouched)
    const isSuperAdmin = currentUser?.role === 'superadmin';
    const [companies, setCompanies] = useState([]);
    // 'all' = Toutes les entreprises (aggregated with company column)
    const [selectedCompanyId, setSelectedCompanyId] = useState('all');
    // Superadmin-only: real share permissions per analysis+company.
    // Backend forces sharedPermission='read-write' on list fetches, so this map
    // ({ [analysisId]: { [targetCompanyId]: 'read-only' | 'read-write' } })
    // drives the share-aware guard for the superadmin path. Admin path untouched.
    const [sharePermissionMap, setSharePermissionMap] = useState({});

    // Fetch data on component mount
    useEffect(() => {
        fetchData();
        checkOllamaStatus();
        if (currentUser?.role === 'superadmin') {
            fetchCompanies();
        }
    }, []);

    // Check Ollama server status
    const checkOllamaStatus = useCallback(async () => {
        setIsLoadingOllama(true);
        try {
            const response = await aiSettingsService.getOllamaStatus();
            console.log('🔍 RAW OLLAMA RESPONSE:', response);

            if (response && response.success && response.data) {
                console.log('✅ Setting valid status from data:', response.data);
                setOllamaStatus(response.data);
            } else if (response && response.available !== undefined) {
                console.log('✅ Setting valid status from raw:', response);
                setOllamaStatus(response);
            } else {
                console.warn('⚠️ Invalid response format:', response);
                setOllamaStatus({
                    available: false,
                    models: [],
                    debugError: 'Format mismatch',
                    debugRaw: JSON.stringify(response).substring(0, 100)
                });
            }
        } catch (error) {
            console.error('Error checking Ollama status:', error);
            setOllamaStatus({
                available: false,
                models: [],
                debugError: error.message
            });
        } finally {
            setIsLoadingOllama(false);
        }
    }, []);

    // Superadmin-only: list companies for the picker (same call as CompanyManagement)
    const fetchCompanies = useCallback(async () => {
        try {
            const response = await companyService.getCompanies({ limit: 1000 });
            const list = response?.data?.data || response?.data || [];
            setCompanies(Array.isArray(list) ? list : []);
        } catch (error) {
            console.error('Error fetching companies:', error);
        }
    }, []);

    // Superadmin-only: resolve a company id (string or populated object) to its name
    const getCompanyName = useCallback((companyId) => {
        if (!companyId) return '';
        const id = typeof companyId === 'object'
            ? (companyId._id || companyId.id || companyId.toString())
            : companyId;
        const found = companies.find(c => {
            const cid = c._id || c.id;
            return cid === id || cid?.toString() === id?.toString();
        });
        return found ? found.name : '';
    }, [companies]);

    // Superadmin-only: user company label (aggregate view)
    const getUserCompanyName = useCallback((u) => {
        if (u.companyName) return u.companyName;
        return getCompanyName(u.companyId);
    }, [getCompanyName]);

    // Superadmin-only: effective shared permission for (analysis, user).
    // Admin path returns analysis.sharedPermission untouched (real data from backend).
    // Superadmin path resolves the real share: scoped to the picked company when one
    // is selected, otherwise to the target user's company. Falls back to
    // analysis.sharedPermission (owner-company analyses have no incoming share).
    const getEffectiveSharedPermission = useCallback((analysis, user) => {
        if (!isSuperAdmin) return analysis?.sharedPermission;
        const analysisId = (analysis._id || analysis.id)?.toString?.() ?? analysis._id ?? analysis.id;
        const perCompany = sharePermissionMap[analysisId];
        if (!perCompany) return analysis?.sharedPermission;
        const scopeCompanyId = (selectedCompanyId && selectedCompanyId !== 'all')
            ? selectedCompanyId
            : (typeof user?.companyId === 'object'
                ? (user.companyId._id || user.companyId.id)
                : user?.companyId);
        const scopeKey = scopeCompanyId?.toString?.() ?? scopeCompanyId;
        if (scopeKey && perCompany[scopeKey]) return perCompany[scopeKey];
        if (!scopeKey) {
            // Aggregate view, user company unknown: most restrictive share wins
            return Object.values(perCompany).includes('read-only') ? 'read-only' : 'read-write';
        }
        return analysis?.sharedPermission;
    }, [isSuperAdmin, sharePermissionMap, selectedCompanyId]);

    // Function to fetch all required data
    // companyOverride is superadmin-only (picker selection); admin path always uses currentUser.companyId
    const fetchData = async (companyOverride) => {
        setIsLoading(true);
        setError(null);

        try {
            const activeCompanyId = companyOverride !== undefined ? companyOverride : selectedCompanyId;
            const isSA = currentUser?.role === 'superadmin';
            // Backend GET /users defaults limit=10 — raise the cap like the companies fetch
            const userFilters = isSA
                ? (activeCompanyId && activeCompanyId !== 'all' ? { companyId: activeCompanyId, limit: 1000 } : { limit: 1000 })
                : { companyId: currentUser.companyId, limit: 1000 };
            const analysisFilters = (isSA && activeCompanyId && activeCompanyId !== 'all')
                ? { companyId: activeCompanyId }
                : {};
            // Superadmin-only: fetch real shares (superadmin-only route) so the
            // read-write guard sees the true share permission. A shares failure
            // must not break the main fetch — fall back to list sharedPermission.
            const sharesPromise = isSA
                ? analysisSharingService.getShares().catch(err => {
                    console.error('Error fetching analysis shares:', err);
                    return null;
                })
                : Promise.resolve(null);
            const [usersResponse, analysesResponse, sharesResponse] = await Promise.all([
                userService.getUsers(userFilters),
                analysisService.getAnalyses(analysisFilters),
                sharesPromise
            ]);

            if (isSA) {
                const sharesList = sharesResponse?.data?.data || sharesResponse?.data || [];
                const shareMap = {};
                (Array.isArray(sharesList) ? sharesList : []).forEach(share => {
                    const rawAnalysisId = typeof share.analysisId === 'object'
                        ? (share.analysisId?._id || share.analysisId?.id)
                        : share.analysisId;
                    const rawTargetId = typeof share.targetCompanyId === 'object'
                        ? (share.targetCompanyId?._id || share.targetCompanyId?.id)
                        : share.targetCompanyId;
                    if (!rawAnalysisId || !rawTargetId) return;
                    const aKey = rawAnalysisId.toString();
                    const tKey = rawTargetId.toString();
                    if (!shareMap[aKey]) shareMap[aKey] = {};
                    // Most restrictive wins per (analysis, company)
                    if (shareMap[aKey][tKey] !== 'read-only') {
                        shareMap[aKey][tKey] = share.permission || 'read-only';
                    }
                });
                setSharePermissionMap(shareMap);
            }

            if (usersResponse.success) {
                // DEBUG: Log raw data
                console.log('[PORTFOLIO_DEBUG] Raw users response:', usersResponse.data?.length, 'users');
                usersResponse.data?.forEach(user => {
                    console.log(`[PORTFOLIO_DEBUG] User ${user.email}:`, {
                        authorizedAnalyses: user.authorizedAnalyses,
                        count: user.authorizedAnalyses?.length || 0
                    });
                });

                // Filter company-scoped users grantable here: simpleuser, viewer, admin.
                // superadmin stays excluded — global access, never company-scoped.
                const eligibleUsers = (usersResponse.data || []).filter(user =>
                    user.role === 'simpleuser' || user.role === 'viewer' || user.role === 'admin'
                );
                setUsers(eligibleUsers);

                // Initialize permissions state with existing user permissions (new format)
                const permissions = {};
                eligibleUsers.forEach(user => {
                    permissions[user._id] = {};
                    // Viewers are always read-only — coerce any stored read-write entries
                    const forceReadOnly = user.role === 'viewer';
                    // Handle both old format (array of IDs) and new format (array of objects)
                    const authorizedAnalyses = user.authorizedAnalyses || [];
                    console.log(`[PORTFOLIO_DEBUG] Processing ${user.email} with ${authorizedAnalyses.length} analyses`);

                    authorizedAnalyses.forEach(auth => {
                        console.log(`[PORTFOLIO_DEBUG] Auth entry:`, auth);
                        // Check for new format FIRST (object with analysisId and permission)
                        if (auth.analysisId) {
                            // New format - object with analysisId and permission
                            const analysisId = typeof auth.analysisId === 'object'
                                ? (auth.analysisId._id || auth.analysisId.toString())
                                : auth.analysisId;
                            permissions[user._id][analysisId] = forceReadOnly ? 'read-only' : (auth.permission || 'read-write');
                        } else if (typeof auth === 'string') {
                            // Old format - just a string ID
                            permissions[user._id][auth] = forceReadOnly ? 'read-only' : 'read-write';
                        } else if (auth._id) {
                            // Old format - ObjectId
                            const analysisId = auth._id.toString ? auth._id.toString() : auth._id;
                            permissions[user._id][analysisId] = forceReadOnly ? 'read-only' : 'read-write';
                        }
                    });
                });
                setUserPermissions(permissions);

                console.log('[PORTFOLIO_DEBUG] Final permissions state:', permissions);
            }

            if (analysesResponse.success) {
                console.log('[PORTFOLIO_DEBUG] Available analyses:',
                    (analysesResponse.data || []).map(a => ({ id: a.id, name: a.name })));
                const analysesData = analysesResponse.data || [];
                setAnalyses(analysesData);

                // Initialize AI settings from each analysis
                const aiSettings = {};
                const localModels = {};
                analysesData.forEach(analysis => {
                    const analysisId = analysis._id || analysis.id;
                    aiSettings[analysisId] = analysis.aiSettings?.mode || 'local';
                    localModels[analysisId] = analysis.aiSettings?.localModel || 'gemma3:12b';
                });
                setAnalysisAISettings(aiSettings);
                setAnalysisLocalModels(localModels);
                console.log('[PORTFOLIO_DEBUG] AI Settings:', aiSettings);
                console.log('[PORTFOLIO_DEBUG] Local Models:', localModels);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            setError(error.message);
            showErrorToast('Erreur lors du chargement des données');
        } finally {
            setIsLoading(false);
        }
    };

    // Superadmin-only: company picker change re-scopes users/analyses fetching
    const handleCompanyChange = (e) => {
        const value = e.target.value;
        setSelectedCompanyId(value);
        fetchData(value);
    };

    // Toggle user expansion
    const toggleUserExpansion = (userId) => {
        setExpandedUsers(prev => ({
            ...prev,
            [userId]: !prev[userId]
        }));
    };

    // Toggle user selection for bulk operations
    const toggleUserSelection = (userId) => {
        setSelectedUsers(prev => {
            const newSet = new Set(prev);
            if (newSet.has(userId)) {
                newSet.delete(userId);
            } else {
                newSet.add(userId);
            }
            return newSet;
        });
    };

    // Select all analyses for a user with read-write permission
    const selectAllAnalyses = (userId, permission = 'read-write') => {
        setAnimatingCards(prev => new Set([...prev, `${userId}-all`]));

        // Viewers are always read-only — coerce regardless of requested permission
        const targetUser = users.find(u => u._id === userId);
        const effectiveDefault = targetUser?.role === 'viewer' ? 'read-only' : permission;

        setUserPermissions(prevPermissions => {
            const newPermissions = { ...prevPermissions };
            newPermissions[userId] = { ...newPermissions[userId] };
            analyses.forEach(analysis => {
                const analysisId = analysis._id || analysis.id;
                // Respect read-only shared permission
                const effectivePermission = (effectiveDefault === 'read-write' && analysis.sharedPermission === 'read-only')
                    ? 'read-only'
                    : effectiveDefault;
                newPermissions[userId][analysisId] = effectivePermission;
            });
            return newPermissions;
        });

        setTimeout(() => {
            setAnimatingCards(prev => {
                const newSet = new Set(prev);
                newSet.delete(`${userId}-all`);
                return newSet;
            });
        }, 300);
    };

    // Deselect all analyses for a user
    const deselectAllAnalyses = (userId) => {
        setAnimatingCards(prev => new Set([...prev, `${userId}-none`]));

        setUserPermissions(prevPermissions => {
            const newPermissions = { ...prevPermissions };
            // Keep only analyses owned by the user
            const ownedAnalyses = analyses
                .filter(analysis => analysis.createdBy === userId)
                .map(analysis => analysis._id || analysis.id);

            newPermissions[userId] = {};
            ownedAnalyses.forEach(analysisId => {
                newPermissions[userId][analysisId] = 'read-write';
            });
            return newPermissions;
        });

        setTimeout(() => {
            setAnimatingCards(prev => {
                const newSet = new Set(prev);
                newSet.delete(`${userId}-none`);
                return newSet;
            });
        }, 300);
    };

    // Handle permission change for a user - now with permission level
    const handlePermissionChange = (userId, analysisId, permission) => {
        // permission can be: 'read-write', 'read-only', or null (no access)
        setAnimatingCards(prev => new Set([...prev, `${userId}-${analysisId}`]));

        setUserPermissions(prevPermissions => {
            const newPermissions = { ...prevPermissions };
            newPermissions[userId] = { ...newPermissions[userId] };

            if (permission === null) {
                // Remove access
                delete newPermissions[userId][analysisId];
            } else {
                // Set permission
                newPermissions[userId][analysisId] = permission;
            }

            return newPermissions;
        });

        // Remove animation after a short delay
        setTimeout(() => {
            setAnimatingCards(prev => {
                const newSet = new Set(prev);
                newSet.delete(`${userId}-${analysisId}`);
                return newSet;
            });
        }, 300);
    };

    // Toggle access (checkbox) - adds with read-write or removes (viewers always get read-only)
    const toggleAccess = (userId, analysisId, hasAccess) => {
        if (hasAccess) {
            const analysis = analyses.find(a => (a._id || a.id) === analysisId);
            const targetUser = users.find(u => u._id === userId);
            const defaultPermission = (targetUser?.role === 'viewer' || analysis?.sharedPermission === 'read-only') ? 'read-only' : 'read-write';
            handlePermissionChange(userId, analysisId, defaultPermission);
        } else {
            handlePermissionChange(userId, analysisId, null);
        }
    };

    // Save permissions for a specific user
    const saveUserPermissions = async (userId) => {
        setIsSaving(true);

        try {
            // Convert the object format to array format for the API
            const userPerms = userPermissions[userId] || {};
            const authorizedAnalyses = Object.entries(userPerms)
                .filter(([_, permission]) => permission !== null)
                .map(([analysisId, permission]) => ({
                    analysisId,
                    permission
                }));

            const response = await userService.updateUserPermissions(userId, {
                authorizedAnalyses
            });

            if (response.success) {
                showSuccessToast('Permissions mises à jour avec succès');
            } else {
                throw new Error(response.message || 'Erreur lors de la sauvegarde');
            }
        } catch (error) {
            console.error('Error saving permissions:', error);
            showErrorToast(error.message);
        } finally {
            setIsSaving(false);
        }
    };

    // Save all permissions
    const saveAllPermissions = async () => {
        setIsSaving(true);

        try {
            const promises = users.map(user => {
                const userPerms = userPermissions[user._id] || {};
                const authorizedAnalyses = Object.entries(userPerms)
                    .filter(([_, permission]) => permission !== null)
                    .map(([analysisId, permission]) => ({
                        analysisId,
                        permission
                    }));

                return userService.updateUserPermissions(user._id, {
                    authorizedAnalyses
                });
            });

            await Promise.all(promises);
            showSuccessToast('Toutes les permissions ont été mises à jour');
        } catch (error) {
            console.error('Error saving all permissions:', error);
            showErrorToast('Erreur lors de la sauvegarde des permissions');
        } finally {
            setIsSaving(false);
        }
    };

    // Filter users based on search term
    const filteredUsers = users.filter(user => {
        const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
        const email = user.email.toLowerCase();
        const search = searchTerm.toLowerCase();

        return fullName.includes(search) || email.includes(search);
    });

    // Get permission stats for a user
    const getUserPermissionStats = (userId) => {
        const perms = userPermissions[userId] || {};
        const entries = Object.entries(perms);
        return {
            total: entries.length,
            readWrite: entries.filter(([_, p]) => p === 'read-write').length,
            readOnly: entries.filter(([_, p]) => p === 'read-only').length
        };
    };

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">
                                Erreur de chargement
                            </h3>
                            <div className="mt-2 text-sm text-red-700">
                                {error}
                            </div>
                            <div className="mt-4">
                                <button
                                    onClick={() => fetchData()}
                                    className="bg-red-100 px-3 py-2 rounded-md text-sm font-medium text-red-800 hover:bg-red-200"
                                >
                                    Réessayer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-8">
                <div className="flex items-center space-x-3 mb-4">
                    <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                            <ShieldCheckIcon className="w-6 h-6 text-white" />
                        </div>
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-gray-900">Portfolio</h1>
                        <p className="text-sm text-gray-500 mt-0.5">Gestion des portfolios d'analyses</p>
                    </div>
                </div>
                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <UserGroupIcon className="h-5 w-5 text-blue-400" />
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-blue-700">
                                <strong>Fonctionnement :</strong> Pour chaque analyse, vous pouvez définir :
                            </p>
                            <div className="flex flex-wrap gap-2 mt-2">
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-green-100 text-green-800 text-xs font-medium">
                                    <PencilSquareIcon className="w-3 h-3 mr-1" /> Lecture/Écriture
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-orange-100 text-orange-800 text-xs font-medium">
                                    <EyeIcon className="w-3 h-3 mr-1" /> Lecture seule
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs font-medium">
                                    <CloudIcon className="w-3 h-3 mr-1" /> API Externe (Gemini)
                                </span>
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-purple-100 text-purple-800 text-xs font-medium">
                                    <ServerIcon className="w-3 h-3 mr-1" /> IA Locale (Ollama)
                                </span>
                            </div>
                            {/* Ollama Status Indicator */}
                            <div className="flex items-center space-x-2 mt-3">
                                <CpuChipIcon className="w-4 h-4 text-purple-500" />
                                <span className="text-xs text-gray-600">IA Locale (Ollama):</span>
                                {isLoadingOllama ? (
                                    <ArrowPathIcon className="w-4 h-4 text-gray-400 animate-spin" />
                                ) : ollamaStatus.available ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                        <span className="w-2 h-2 bg-green-500 rounded-full mr-1 animate-pulse"></span>
                                        Disponible ({ollamaStatus.modelsCount || 0} modèles)
                                    </span>
                                ) : (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                        <span className="w-2 h-2 bg-red-500 rounded-full mr-1"></span>
                                        Non disponible
                                    </span>
                                )}
                                <button
                                    onClick={checkOllamaStatus}
                                    className="p-1 hover:bg-gray-200 rounded transition-colors"
                                    title="Rafraîchir le statut Ollama"
                                >
                                    <ArrowPathIcon className={`w-3 h-3 text-gray-500 ${isLoadingOllama ? 'animate-spin' : ''}`} />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Enhanced Search and Controls */}
            <div className="mb-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="flex flex-col space-y-4">
                    {/* Superadmin-only: company scope picker */}
                    {isSuperAdmin && (
                        <div className="max-w-md">
                            <label htmlFor="portfolio-company-filter" className="block text-sm font-medium text-gray-700 mb-2">
                                Entreprise
                            </label>
                            <select
                                id="portfolio-company-filter"
                                value={selectedCompanyId}
                                onChange={handleCompanyChange}
                                className="block w-full px-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                            >
                                <option value="all">Toutes les entreprises</option>
                                {companies.map(company => (
                                    <option key={company._id || company.id} value={company._id || company.id}>
                                        {company.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    {/* Primary Controls Row */}
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                        <div className="flex-1 max-w-md">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Rechercher des utilisateurs
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="text"
                                    placeholder="Nom, prénom ou email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                                />
                            </div>
                        </div>

                        <div className="flex items-center space-x-3">
                            <button
                                onClick={saveAllPermissions}
                                disabled={isSaving}
                                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
                            >
                                {isSaving ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Sauvegarde...
                                    </>
                                ) : (
                                    <>
                                        <CheckIcon className="w-4 h-4 mr-2" />
                                        Sauvegarder Tout
                                    </>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Stats Row */}
                    {filteredUsers.length > 0 && (
                        <div className="pt-4 border-t border-gray-200">
                            <div className="flex items-center justify-between text-sm text-gray-600">
                                <span>{filteredUsers.length} utilisateur(s) trouvé(s)</span>
                                <span>{analyses.length} analyse(s) disponible(s)</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Users and Permissions */}
            <div className="space-y-6">
                {filteredUsers.length === 0 ? (
                    <div className="text-center py-12">
                        <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">Aucun utilisateur trouvé</h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {searchTerm ? 'Essayez de modifier votre recherche.' : (isSuperAdmin ? 'Aucun utilisateur éligible pour cette sélection.' : "Aucun utilisateur simple dans votre entreprise.")}
                        </p>
                    </div>
                ) : (
                    filteredUsers.map(user => {
                        const isExpanded = expandedUsers[user._id] || false;
                        const isSelected = selectedUsers.has(user._id);
                        const userPerms = userPermissions[user._id] || {};
                        const stats = getUserPermissionStats(user._id);
                        const ownedAnalyses = analyses.filter(analysis => analysis.createdBy === user._id);

                        return (
                            <div
                                key={user._id}
                                className={`bg-white rounded-xl shadow-sm border transition-all duration-300 hover:shadow-md ${isSelected ? 'border-blue-300 ring-2 ring-blue-100' : 'border-gray-200'
                                    }`}
                            >
                                {/* User Header */}
                                <div className="p-6 border-b border-gray-100">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-4">
                                            {bulkMode && (
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected}
                                                    onChange={() => toggleUserSelection(user._id)}
                                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                />
                                            )}
                                            <div className="flex-shrink-0">
                                                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                                    <UserIcon className="w-5 h-5 text-blue-600" />
                                                </div>
                                            </div>
                                            <div>
                                                <h3 className="text-lg font-semibold text-gray-900">
                                                    {user.firstName} {user.lastName}
                                                </h3>
                                                <p className="text-sm text-gray-500">{user.email}</p>
                                                {isSuperAdmin && (
                                                    <span className="inline-flex items-center px-2 py-0.5 mt-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700">
                                                        {getUserCompanyName(user) || 'Entreprise inconnue'}
                                                    </span>
                                                )}
                                                <div className="flex items-center space-x-4 mt-1">
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                        {ownedAnalyses.length} créée(s)
                                                    </span>
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                        <PencilSquareIcon className="w-3 h-3 mr-1" />
                                                        {stats.readWrite} Écriture
                                                    </span>
                                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                                        <EyeIcon className="w-3 h-3 mr-1" />
                                                        {stats.readOnly} Lecture
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            {user.role !== 'viewer' && (
                                            <button
                                                onClick={() => selectAllAnalyses(user._id, 'read-write')}
                                                className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-md text-sm font-medium hover:bg-green-200 transition-colors"
                                                title="Accorder l'accès en écriture à toutes les analyses"
                                            >
                                                <PencilSquareIcon className="w-4 h-4 mr-1" />
                                                Tout (R/W)
                                            </button>
                                            )}
                                            <button
                                                onClick={() => selectAllAnalyses(user._id, 'read-only')}
                                                className="inline-flex items-center px-3 py-1 bg-orange-100 text-orange-700 rounded-md text-sm font-medium hover:bg-orange-200 transition-colors"
                                                title="Accorder l'accès en lecture seule à toutes les analyses"
                                            >
                                                <EyeIcon className="w-4 h-4 mr-1" />
                                                Tout (R)
                                            </button>
                                            <button
                                                onClick={() => deselectAllAnalyses(user._id)}
                                                className="inline-flex items-center px-3 py-1 bg-gray-100 text-gray-700 rounded-md text-sm font-medium hover:bg-gray-200 transition-colors"
                                            >
                                                <EyeSlashIcon className="w-4 h-4 mr-1" />
                                                Aucune
                                            </button>
                                            <button
                                                onClick={() => saveUserPermissions(user._id)}
                                                disabled={isSaving}
                                                className="inline-flex items-center px-3 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50 transition-colors"
                                            >
                                                <CheckIcon className="w-4 h-4 mr-1" />
                                                Sauvegarder
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Analysis Permissions - Always Visible */}
                                <div className="px-6 pb-6">
                                    <div className="border-t border-gray-200 pt-4">
                                        <h4 className="text-sm font-medium text-gray-900 mb-4">
                                            Portfolio d'analyses
                                        </h4>

                                        {analyses.length === 0 ? (
                                            <div className="text-center py-8">
                                                <DocumentTextIcon className="mx-auto h-8 w-8 text-gray-400" />
                                                <p className="mt-2 text-sm text-gray-500">Aucune analyse disponible</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                                {analyses.map(analysis => {
                                                    const analysisId = analysis._id || analysis.id;
                                                    const permission = userPerms[analysisId] || null;
                                                    const hasAccess = permission !== null;
                                                    const isOwner = analysis.createdBy === user._id;
                                                    const isAnimating = animatingCards.has(`${user._id}-${analysisId}`);
                                                    // Real share for the guard: provider admins read it off the
                                                    // analysis; superadmin resolves it from the shares map
                                                    // (list sharedPermission is forced read-write for superadmin).
                                                    const effectiveShare = getEffectiveSharedPermission(analysis, user);
                                                    const shareBlocksWrite = effectiveShare === 'read-only' && !isSuperAdmin;
                                                    const shareOverride = effectiveShare === 'read-only' && isSuperAdmin;

                                                    // Debug - log first few
                                                    if (!window._debugLogged && analysis.name === 'Analyse') {
                                                        console.log(`[PORTFOLIO_DEBUG] user._id:`, user._id);
                                                        console.log(`[PORTFOLIO_DEBUG] userPerms for this user:`, userPerms);
                                                        console.log(`[PORTFOLIO_DEBUG] Looking up analysisId: "${analysisId}"`);
                                                        console.log(`[PORTFOLIO_DEBUG] Keys in userPerms:`, Object.keys(userPerms));
                                                        console.log(`[PORTFOLIO_DEBUG] Result: permission=${permission}, hasAccess=${hasAccess}`);
                                                        window._debugLogged = true;
                                                    }

                                                    return (
                                                        <div
                                                            key={analysisId}
                                                            className={`flex items-center p-3 rounded-lg border transition-all duration-200 ${isOwner
                                                                ? 'bg-blue-50 border-blue-200'
                                                                : permission === 'read-write'
                                                                    ? 'bg-green-50 border-green-200 hover:bg-green-100'
                                                                    : permission === 'read-only'
                                                                        ? 'bg-orange-50 border-orange-200 hover:bg-orange-100'
                                                                        : 'bg-white border-gray-200 hover:bg-gray-50'
                                                                } ${isAnimating ? 'scale-105 shadow-md' : ''}`}
                                                        >
                                                            {/* Access checkbox */}
                                                            <input
                                                                id={`user-${user._id}-analysis-${analysisId}`}
                                                                type="checkbox"
                                                                checked={hasAccess || isOwner}
                                                                disabled={isOwner}
                                                                onChange={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleAccess(user._id, analysisId, e.target.checked);
                                                                }}
                                                                className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
                                                            />

                                                            {/* Analysis name */}
                                                            <div className="ml-3 flex-1">
                                                                <label
                                                                    htmlFor={`user-${user._id}-analysis-${analysisId}`}
                                                                    className={`block text-sm font-medium cursor-pointer ${isOwner ? 'text-blue-800' : 'text-gray-900'
                                                                        }`}
                                                                >
                                                                    {analysis.name}
                                                                </label>
                                                                {analysis.mission && (
                                                                    <p className="text-xs text-gray-500 mt-1 truncate">
                                                                        {analysis.mission}
                                                                    </p>
                                                                )}
                                                                {isSuperAdmin && selectedCompanyId === 'all' && analysis.companyId && (
                                                                    <p className="text-xs text-gray-400 mt-0.5">
                                                                        {getCompanyName(analysis.companyId)}
                                                                    </p>
                                                                )}
                                                            </div>

                                                            {/* Permission dropdown */}
                                                            {(hasAccess || isOwner) && (
                                                                <div className="ml-4">
                                                                    {isOwner ? (
                                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-200 text-blue-800">
                                                                            <LockClosedIcon className="w-3 h-3 mr-1" />
                                                                            Propriétaire
                                                                        </span>
                                                                    ) : (
                                                                        <select
                                                                            value={permission || 'read-write'}
                                                                            onChange={(e) => handlePermissionChange(user._id, analysisId, e.target.value)}
                                                                            className={`text-xs font-medium px-2 py-1 rounded-md border-0 cursor-pointer focus:ring-2 focus:ring-blue-500 ${permission === 'read-write'
                                                                                ? 'bg-green-100 text-green-800'
                                                                                : 'bg-orange-100 text-orange-800'
                                                                                }`}
                                                                        >
                                                                            <option 
                                                                                value="read-write" 
                                                                                disabled={shareBlocksWrite}
                                                                                title={shareBlocksWrite ? "Accès restreint par l'entreprise" : shareOverride ? "Dérogation superadmin : écriture malgré le partage en lecture seule" : ""}
                                                                            >
                                                                                ✏️ Lecture/Écriture {shareBlocksWrite && '(Restreint)'}{shareOverride && '(Dérogation)'}
                                                                            </option>
                                                                            <option value="read-only">👁️ Lecture seule</option>
                                                                        </select>
                                                                    )}
                                                                </div>
                                                            )}

                                                            {/* AI Mode dropdown */}
                                                            <div className="ml-3 flex items-center space-x-2">
                                                                <select
                                                                    value={analysisAISettings[analysisId] || 'local'}
                                                                    onChange={async (e) => {
                                                                        const newMode = e.target.value;
                                                                        const currentModel = analysisLocalModels[analysisId] || 'gemma3:12b';
                                                                        // Optimistic update
                                                                        setAnalysisAISettings(prev => ({
                                                                            ...prev,
                                                                            [analysisId]: newMode
                                                                        }));
                                                                        // Save to backend immediately
                                                                        try {
                                                                            await aiSettingsService.updateAnalysisAIMode(analysisId, newMode, currentModel);
                                                                            showSuccessToast(`Mode IA changé pour ${newMode === 'local' ? 'IA Locale' : 'API Externe'}`);
                                                                        } catch (error) {
                                                                            // Revert on error
                                                                            setAnalysisAISettings(prev => ({
                                                                                ...prev,
                                                                                [analysisId]: newMode === 'local' ? 'external' : 'local'
                                                                            }));
                                                                            showErrorToast(error.response?.data?.message || 'Erreur lors du changement de mode IA');
                                                                        }
                                                                    }}
                                                                    className={`text-xs font-medium px-2 py-1 rounded-md border-0 cursor-pointer focus:ring-2 focus:ring-purple-500 ${analysisAISettings[analysisId] === 'local'
                                                                        ? 'bg-purple-100 text-purple-800'
                                                                        : 'bg-blue-100 text-blue-800'
                                                                        }`}
                                                                >
                                                                    <option value="external">☁️ API Externe</option>
                                                                    <option value="local" disabled={!ollamaStatus.available}>
                                                                        🖥️ IA Locale {!ollamaStatus.available && '(⚠️)'}
                                                                    </option>
                                                                </select>

                                                                {/* Model selector - only visible when local mode is active */}
                                                                {analysisAISettings[analysisId] === 'local' && ollamaStatus.available && (
                                                                    <select
                                                                        value={analysisLocalModels[analysisId] || 'gemma3:12b'}
                                                                        onChange={async (e) => {
                                                                            const newModel = e.target.value;
                                                                            // Optimistic update
                                                                            setAnalysisLocalModels(prev => ({
                                                                                ...prev,
                                                                                [analysisId]: newModel
                                                                            }));
                                                                            // Save to backend
                                                                            try {
                                                                                await aiSettingsService.updateAnalysisAIMode(analysisId, 'local', newModel);
                                                                                showSuccessToast(`Modèle IA changé: ${newModel}`);
                                                                            } catch (error) {
                                                                                showErrorToast('Erreur lors du changement de modèle');
                                                                            }
                                                                        }}
                                                                        className="text-xs font-medium px-2 py-1 rounded-md border-0 cursor-pointer bg-violet-100 text-violet-800 focus:ring-2 focus:ring-violet-500"
                                                                        title="Choisir le modèle IA local"
                                                                    >
                                                                        {ollamaStatus.models && ollamaStatus.models.length > 0 ? (
                                                                            ollamaStatus.models.map(model => (
                                                                                <option key={model.name} value={model.name}>
                                                                                    🤖 {model.name} {model.size ? `(${model.size})` : ''}
                                                                                </option>
                                                                            ))
                                                                    ) : user.role === 'viewer' ? (
                                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800" title="Les lecteurs sont toujours en lecture seule">
                                                                            👁️ Lecture seule
                                                                        </span>
                                                                    ) : (
                                                                            <option value="gemma3:12b">🤖 gemma3:12b</option>
                                                                        )}
                                                                    </select>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default PortfolioManagement;